import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft,
  FiEye,
  FiEyeOff,
  FiCheck,
  FiUpload,
  FiPlus,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { isAuthenticated } from '../../lib/auth';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import styles from './OrderDetail.module.css';

interface OrderData {
  id: string;
  orderNumber: string;
  customer: {
    id: string;
    email: string;
    name?: string | null;
    language?: string;
  };
  country: string;
  quantity: number;
  withAccount: boolean;
  totalAmount: number;
  discountAmount?: number;
  status: string;
  paymentStatus?: string | null;
  paymentId?: string | null;
  createdAt: string;
  promoCode?: {
    id: string;
    code: string;
    discountPercent: number;
    affiliateId?: string | null;
    affiliate?: {
      id: string;
      email: string;
      name?: string | null;
    } | null;
  } | null;
  affiliateCommission?: {
    id: string;
    commissionAmount: number;
    commissionRate: number;
    orderAmount: number;
    createdAt: string;
  } | null;
  accounts?: Array<{ 
    id: string;
    username: string; 
    password: string;
    email?: string | null;
    phone?: string | null;
    notes?: string | null;
  }>;
  auditLogs?: Array<{ 
    id: string;
    action: string; 
    createdAt: string;
    details?: string;
    adminUser?: {
      email: string;
      name?: string | null;
    };
  }>;
}

const statusOrder = ['pending', 'delivered'];

const OrderDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderData | null>(null);
  const [showCredentials, setShowCredentials] = useState(false);
  const [newCredentials, setNewCredentials] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [adminRole, setAdminRole] = useState<string>('');

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    
    // Fetch admin role
    api.get('/auth/me')
      .then((res) => setAdminRole(res.data.user.role))
      .catch((err) => console.error('Failed to fetch user:', err));
  }, [navigate]);

  const fetchOrder = React.useCallback(async () => {
    try {
      const res = await api.get(`/admin/orders/${id}`);
      setOrder(res.data.order || res.data);
    } catch (err) {
      console.error('Failed to fetch order:', err);
      // Demo data
      setOrder({
        id: id || '1',
        orderNumber: 'ORD-A1B2C',
        customer: {
          id: '1',
          email: 'client@example.com',
          name: 'Client Demo',
        },
        country: 'FR',
        quantity: 10,
        withAccount: true,
        totalAmount: 110,
        status: 'processing',
        createdAt: new Date().toISOString(),
        accounts: [
          { id: '1', username: 'user_demo_1', password: 'password123', email: null, phone: null, notes: null },
          { id: '2', username: 'user_demo_2', password: 'password456', email: null, phone: null, notes: null },
        ],
        auditLogs: [
          { id: '1', action: 'Order created', createdAt: new Date(Date.now() - 86400000).toISOString(), details: 'Order created' },
          { id: '2', action: 'Payment confirmed', createdAt: new Date(Date.now() - 72000000).toISOString(), details: 'Payment confirmed' },
          { id: '3', action: 'Status changed to processing', createdAt: new Date(Date.now() - 36000000).toISOString(), details: 'Status changed' },
        ],
      });
    }
  }, [id]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    
    // Fetch admin role
    api.get('/auth/me')
      .then((res) => setAdminRole(res.data.user.role))
      .catch((err) => console.error('Failed to fetch user:', err));
    
    fetchOrder();
  }, [navigate, fetchOrder]);

  const handleAddCredentials = async () => {
    if (!newCredentials.trim()) return;
    try {
      await api.put(`/admin/orders/${id}/accounts`, {
        csv: newCredentials,
      });
      toast.success('Credentials added');
      setNewCredentials('');
      setShowAddForm(false);
      fetchOrder();
    } catch {
      toast.error('Failed to add credentials');
    }
  };

  const handleMarkDelivered = async () => {
    try {
      await api.put(`/admin/orders/${id}/ready`);
      toast.success('Order marked as delivered');
      fetchOrder();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to update status';
      toast.error(errorMsg);
    }
  };

  const handleForcePayment = async () => {
    if (!window.confirm('Are you sure you want to manually validate this payment? This will:\n- Mark order as PAID\n- Create affiliate commission if promo code used\n- Auto-fulfill from stock if available')) {
      return;
    }
    
    try {
      const res = await api.post(`/admin/orders/${id}/force-payment`);
      toast.success(res.data.message || 'Payment validated successfully');
      fetchOrder();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to validate payment';
      toast.error(errorMsg);
    }
  };

  const currentStatusIdx = order ? statusOrder.indexOf(order.status) : -1;

  if (!order) return null;

  return (
    <AdminLayout>
      <Link to="/admin/orders" className={styles.backLink}>
        <FiArrowLeft size={16} />
        {t('common.back')}
      </Link>

        <div className={styles.header}>
          <h1>{t('admin.orderDetail' as string) || 'Order Detail'} #{order.orderNumber}</h1>
          <div className={styles.headerActions}>
            {order.status === 'PENDING_PAYMENT' && order.paymentStatus && (
              <button 
                className={styles.forcePaymentBtn} 
                onClick={handleForcePayment}
                style={{ 
                  backgroundColor: 'var(--color-warning)', 
                  marginRight: '8px' 
                }}
              >
                <FiCheck size={16} />
                {t('admin.forcePayment' as string) || 'Force Payment'}
              </button>
            )}
            {order.status !== 'DELIVERED' && order.status !== 'PENDING_PAYMENT' && (
              <button className={styles.markReadyBtn} onClick={handleMarkDelivered}>
                <FiCheck size={16} />
                {t('admin.markDelivered' as string) || 'Mark as Delivered'}
              </button>
            )}
          </div>
        </div>

        {/* Payment Issue Alert */}
        {order.status === 'PENDING_PAYMENT' && order.paymentStatus === 'partially_paid' && (
          <div style={{
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
            border: '1px solid var(--color-warning)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <div>
              <strong style={{ color: 'var(--color-warning)' }}>
                {t('admin.partialPayment' as string) || 'Partial Payment Detected'}
              </strong>
              <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: 'var(--color-gray-300)' }}>
                {t('admin.partialPaymentHelp' as string) || 
                  'This order has a partial payment. Review the payment details and click "Force Payment" to validate if the amount is acceptable (≥95%).'}
              </p>
            </div>
          </div>
        )}
        
        {order.status === 'PENDING_PAYMENT' && order.paymentStatus && order.paymentStatus !== 'partially_paid' && order.paymentStatus !== 'waiting' && (
          <div style={{
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
            border: '1px solid var(--color-danger)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>❌</span>
            <div>
              <strong style={{ color: 'var(--color-danger)' }}>
                {t('admin.paymentIssue' as string) || 'Payment Issue'}
              </strong>
              <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: 'var(--color-gray-300)' }}>
                Payment status: <strong>{order.paymentStatus}</strong>. Check the payment provider dashboard.
              </p>
            </div>
          </div>
        )}

        <div className={styles.grid}>
          {/* Order Info */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>{t('admin.orderDetail' as string) || 'Order Info'}</h3>
              <span className={`${styles.statusBadge} ${styles[order.status as keyof typeof styles] || ''}`}>
                {t(`orderStatus.${order.status}`)}
              </span>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.infoRow}>
                <span>{t('orderStatus.orderNumber')}</span>
                <span>{order.orderNumber}</span>
              </div>
              <div className={styles.infoRow}>
                <span>{t('checkout.country' as string) || 'Country'}</span>
                <span>{order.country === 'FR' ? 'France' : 'USA'}</span>
              </div>
              <div className={styles.infoRow}>
                <span>{t('checkout.quantity' as string) || 'Quantity'}</span>
                <span>{order.quantity}</span>
              </div>
              <div className={styles.infoRow}>
                <span>Type</span>
                <span>{order.withAccount ? t('checkout.withAccount') : t('checkout.withoutAccount')}</span>
              </div>
              <div className={styles.infoRow}>
                <span>{t('checkout.total' as string) || 'Total'}</span>
                <span>${order.totalAmount?.toFixed(2) || '0.00'}</span>
              </div>
              {order.paymentId && (
                <div className={styles.infoRow}>
                  <span>{t('admin.paymentId' as string) || 'Payment ID'}</span>
                  <span style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                    {order.paymentId}
                  </span>
                </div>
              )}
              {order.paymentStatus && (
                <div className={styles.infoRow}>
                  <span>{t('admin.paymentStatus' as string) || 'Payment Status'}</span>
                  <span style={{ 
                    fontWeight: 600,
                    color: order.paymentStatus === 'finished' ? 'var(--color-success)' : 
                           order.paymentStatus === 'partially_paid' ? 'var(--color-warning)' :
                           order.paymentStatus === 'waiting' ? 'var(--color-gray-400)' :
                           'var(--color-danger)'
                  }}>
                    {order.paymentStatus}
                  </span>
                </div>
              )}
              {order.promoCode && (
                <>
                  <div className={styles.infoRow}>
                    <span>{t('admin.promoCode' as string) || 'Promo Code'}</span>
                    <span style={{ fontWeight: 600, color: 'var(--color-primary)' }}>
                      {order.promoCode.code} (-{order.promoCode.discountPercent}%)
                    </span>
                  </div>
                  {order.discountAmount && order.discountAmount > 0 && (
                    <div className={styles.infoRow}>
                      <span>{t('admin.discount' as string) || 'Discount'}</span>
                      <span style={{ color: 'var(--color-success)' }}>
                        -${order.discountAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                </>
              )}
              {order.affiliateCommission && (
                <>
                  <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid var(--color-gray-700)' }} />
                  <div className={styles.infoRow}>
                    <span>{t('admin.affiliate' as string) || 'Affiliate'}</span>
                    <span>
                      {order.promoCode?.affiliate?.name || order.promoCode?.affiliate?.email || 'Unknown'}
                    </span>
                  </div>
                  <div className={styles.infoRow}>
                    <span>{t('admin.commissionPaid' as string) || 'Commission Paid'}</span>
                    <span style={{ fontWeight: 600, color: 'var(--color-warning)' }}>
                      ${order.affiliateCommission.commissionAmount.toFixed(2)} ({(order.affiliateCommission.commissionRate * 100).toFixed(0)}%)
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Customer Info */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>{t('admin.customerInfo' as string) || 'Customer'}</h3>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.infoRow}>
                <span>Email</span>
                <span>{order.customer.email}</span>
              </div>
              {order.customer.name && (
                <div className={styles.infoRow}>
                  <span>{t('checkout.name')}</span>
                  <span>{order.customer.name}</span>
                </div>
              )}
              <div className={styles.infoRow}>
                <span>Date</span>
                <span>{new Date(order.createdAt).toLocaleString()}</span>
              </div>
            </div>
            {/* Status Timeline */}
            <div className={styles.timeline}>
              {statusOrder.map((status, i) => {
                const isDone = i < currentStatusIdx;
                const isCurrent = i === currentStatusIdx;
                const isLast = i === statusOrder.length - 1;
                return (
                  <div key={status} className={styles.timelineItem}>
                    <div className={styles.timelineDotWrap}>
                      <div
                        className={`${styles.timelineDot} ${
                          isDone ? styles.done : isCurrent ? styles.current : ''
                        }`}
                      />
                      {!isLast && (
                        <div className={`${styles.timelineBar} ${isDone ? styles.done : ''}`} />
                      )}
                    </div>
                    <div>
                      <div className={`${styles.timelineLabel} ${isCurrent ? styles.active : ''}`}>
                        {t(`orderStatus.${status}`)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Credentials - Hidden for MANAGER role */}
          {adminRole !== 'MANAGER' && (
            <div className={`${styles.card} ${styles.credentialsSection}`}>
              <div className={styles.cardHeader}>
                <h3>{t('admin.credentials' as string) || 'Credentials'}</h3>
                <button
                  className={styles.revealBtn}
                  onClick={() => setShowCredentials(!showCredentials)}
                >
                  {showCredentials ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                  {showCredentials
                    ? 'Hide'
                    : (t('admin.revealCredentials'))}
                </button>
              </div>
            <div className={styles.cardBody}>
              {order.accounts && order.accounts.length > 0 ? (
                <div className={styles.credentialsList}>
                  {order.accounts.map((account, i) => (
                    <div key={account.id} className={styles.credentialItem}>
                      <span>{i + 1}.</span>
                      <span>{account.username}</span>
                      <span>:</span>
                      <span className={!showCredentials ? styles.hidden : ''}>
                        {showCredentials ? account.password : '********'}
                      </span>
                      {account.phone && (
                        <>
                          <span>:</span>
                          <span className={!showCredentials ? styles.hidden : ''}>
                            {showCredentials ? account.phone : '********'}
                          </span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: '0.875rem', color: 'var(--color-gray-400)' }}>
                  No credentials added yet
                </p>
              )}

              {/* Add credentials form */}
              {!showAddForm ? (
                <button
                  className={`${styles.addFormBtn} ${styles.secondary}`}
                  onClick={() => setShowAddForm(true)}
                  style={{ marginTop: '16px' }}
                >
                  <FiPlus size={14} />
                  {t('admin.addCredentials')}
                </button>
              ) : (
                <div className={styles.addForm}>
                  <h4>{t('admin.addCredentials')}</h4>
                  <textarea
                    className={styles.addFormTextarea}
                    placeholder={t('admin.pasteCredentials' as string) || 'username:password (one per line)'}
                    value={newCredentials}
                    onChange={(e) => setNewCredentials(e.target.value)}
                  />
                  <div className={styles.addFormActions}>
                    <button
                      className={`${styles.addFormBtn} ${styles.primary}`}
                      onClick={handleAddCredentials}
                    >
                      <FiCheck size={14} />
                      {t('common.save')}
                    </button>
                    <label className={`${styles.addFormBtn} ${styles.secondary}`}>
                      <FiUpload size={14} />
                      {t('admin.importCSV' as string) || 'Import CSV'}
                      <input
                        type="file"
                        accept=".csv,.txt"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (ev) => {
                              setNewCredentials(ev.target?.result as string || '');
                            };
                            reader.readAsText(file);
                          }
                        }}
                      />
                    </label>
                    <button
                      className={`${styles.addFormBtn} ${styles.secondary}`}
                      onClick={() => {
                        setShowAddForm(false);
                        setNewCredentials('');
                      }}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
            </div>
          )}

          {/* Audit Log */}
          <div className={`${styles.card} ${styles.auditSection}`}>
            <div className={styles.cardHeader}>
              <h3>{t('admin.auditLog' as string) || t('admin.audit')}</h3>
            </div>
            <div className={styles.cardBody}>
              {order.auditLogs && order.auditLogs.length > 0 ? (
                <div className={styles.auditList}>
                  {order.auditLogs.map((entry) => (
                    <div key={entry.id} className={styles.auditItem}>
                      <span className={styles.auditTime}>
                        {new Date(entry.createdAt).toLocaleString()}
                      </span>
                      <span className={styles.auditAction}>
                        <strong>{entry.adminUser?.email || entry.adminUser?.name || 'System'}</strong>: {entry.details || entry.action}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: '0.875rem', color: 'var(--color-gray-400)' }}>
                  No audit entries
                </p>
              )}
            </div>
          </div>
        </div>
    </AdminLayout>
  );
};

export default OrderDetail;
